import { toLatinDigits } from '@/lib/format'

const CHAR_MAP: Record<string, string> = {
    'ي': 'ی',
    'ى': 'ی',
    'ك': 'ک',
    'ة': 'ه',
    'أ': 'ا',
    'إ': 'ا',
    'آ': 'ا',
    'ؤ': 'و'
}

const CHAR_VARIANTS: Record<string, string> = {
    'ی': 'یيى',
    'ک': 'کك',
    'ه': 'هة',
    'ا': 'اآأإ',
    'و': 'وؤ'
}

const STOP_WORDS = new Set(['در', 'به', 'از', 'که', 'با', 'را', 'این', 'آن', 'برای', 'تا', 'یا', 'هم', 'های', 'ها'])

export const normalizeText = (value: string): string => {
    return toLatinDigits(value)
        .toLowerCase()
        .replace(/[يىكةأإآؤ]/g, char => CHAR_MAP[char] ?? char)
        .replace(/[ً-ٰٟ]/g, '')
        .replace(/[‌‎‏]/g, '')
}

export const tokenize = (query: string): string[] => {
    return normalizeText(query)
        .split(/[\s،,؛;:!?؟.()«»"]+/)
        .map(token => token.trim())
        .filter(token => token.length > 1 && !STOP_WORDS.has(token))
}

const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const charPattern = (char: string): string => {
    const variants = CHAR_VARIANTS[char]

    if (variants) return `[${variants}]`

    if (/[0-9]/.test(char)) {
        const digit = Number(char)

        return `[${char}${'۰۱۲۳۴۵۶۷۸۹'.charAt(digit)}${'٠١٢٣٤٥٦٧٨٩'.charAt(digit)}]`
    }

    return escapeRegex(char)
}

export const buildHighlighter = (query: string): RegExp | null => {
    const tokens = tokenize(query)

    if (!tokens.length) return null

    const pattern = tokens
        .sort((a, b) => b.length - a.length)
        .map(token => token.split('').map(charPattern).join('‌?'))
        .join('|')

    return new RegExp(`(${pattern})`, 'gi')
}

type Searchable = {
    title: string
    summary: string
    labels: string
    meta: string
}

export const scoreText = (fields: Searchable, tokens: string[]): number => {
    if (!tokens.length) return 0

    const title = normalizeText(fields.title)
    const summary = normalizeText(fields.summary)
    const labels = normalizeText(fields.labels)
    const meta = normalizeText(fields.meta)

    let score = 0

    for (const token of tokens) {
        let tokenScore = 0

        if (title.includes(token)) tokenScore += 6
        if (labels.includes(token)) tokenScore += 4
        if (summary.includes(token)) tokenScore += 3
        if (meta.includes(token)) tokenScore += 1

        if (tokenScore === 0) return 0

        score += tokenScore
    }

    return score
}
